import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewEncapsulation} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Field, FieldAttributes} from '@k2/common/k2-forms-state/types';
import {Subscription} from "rxjs";

@Component({
  selector: 'multi-field',
  templateUrl: 'multi-field.component.html',
  styleUrls: ['multi-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MultiFieldComponent implements OnChanges, OnDestroy {
  @Input('field') mainField: Field;
  @Input() min = 1;
  @Input() max: number | null = null;
  @Output() onFieldUpdate = new EventEmitter<any[]>();
  entryFields: Field[] = [];

  private subscription = new Subscription();

  ngOnChanges(): void {
    this.ensureInitializedFromValue();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  addOption(afterIndex?: number): void {
    if (this.max != null && this.entryFields.length >= this.max) return;
    const insertAt = typeof afterIndex === 'number' ? afterIndex + 1 : this.entryFields.length;
    const newField = this.cloneFieldForIndex('', insertAt);
    this.entryFields.splice(insertAt, 0, newField);

    this.wireValueChanges();
    this.syncParentControl();
  }

  removeOption(index: number): void {
    if (this.entryFields.length <= this.min) return;
    this.entryFields.splice(index, 1);

    this.wireValueChanges();
    this.syncParentControl();
  }
  onChildUpdated(): void {
    this.syncParentControl();
  }

  trackByIndex(i: number): number {
    return i;
  }

  private ensureInitializedFromValue(): void {
    if (!this.mainField) return;

    const normalizedValues = this.normalizeToArray(this.mainField.control?.value);
    while (normalizedValues.length < this.min) normalizedValues.push('');

    this.entryFields = normalizedValues.map((val, i) => this.cloneFieldForIndex(val, i));

    this.wireValueChanges();
    this.syncParentControl();
  }

  private wireValueChanges(): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();

    this.entryFields.forEach(f => {
      const subscription = f.control.valueChanges.subscribe(() => {
        this.syncParentControl();
      });
      this.subscription.add(subscription);
    });
  }

  private normalizeToArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    return [value];
  }

  private cloneFieldForIndex(value: any, index: number): Field {
    const baseControl = this.mainField.control as UntypedFormControl;

    const baseValidators = (baseControl as any)?._rawValidators ?? baseControl?.validator ?? null;
    const validators = baseValidators
      ? (Array.isArray(baseValidators) ? baseValidators : [baseValidators])
      : null;

    const fieldAttributes = this.mainField.attributes as FieldAttributes;

    const showLabel = index === 0;
    const attributes: FieldAttributes = {
      ...fieldAttributes,
      type: fieldAttributes.type ?? 'text' as FieldAttributes['type'],
      id: `${fieldAttributes.id || 'multi-field'}_${index}`,
      label: showLabel ? (fieldAttributes.label ?? '') : '',
    };

    const control = new UntypedFormControl(value, validators ?? undefined);

    return {
      ...this.mainField,
      attributes,
      control,
      defaultValue: value ?? null,
    };
  }

  private syncParentControl(emit: boolean = true): void {
    const values = this.entryFields.map(field => field.control?.value);
    this.mainField.control?.setValue(values, { emitEvent: false });
    if (emit) {
      this.onFieldUpdate.emit(values);
    }
  }
}
